import { ISPListItemRetrieverResponse } from "@ms/odsp-datasources/lib/dataSources/item/spListItemRetriever/SPListItemRetriever";
import { ListViewDataPrefetchHelper } from "@ms/sp-list-prefetch/lib/splistPrefetch/ListViewDataPrefetchHelper";
import { IPrefetchDataOptions } from "@ms/items-view-addon-sp/lib/dataSource/interfaces/IGetServerData";
import { LoggingService } from "@msteams/bridges-services-ts-logging";
import {
  FilesCachePersistor,
  FilesStoresTypes
} from "@msteams/bridges-services-files-cache-persistor";
import { IEventData, ITypedScenario } from "@msteams/bridges-models-ts-logging";
import { FilesPrefetchMemoryRecordService } from "@msteams/bridges-services-files-prefetch-memory-record";
import { scenarios } from "./constants";

/**
 * Called from items view to request for any prefetch data from Teams. In this case we have
 * the data supplied from indexedDB
 * @param options Contains the payload body and url for the request
 * @returns A response of type ISPListItemRetrieverResponse or undefined if we want
 *          items view to go make a network call
 */
export async function getPrefetchListData(
  options: IPrefetchDataOptions
): Promise<ISPListItemRetrieverResponse | undefined> {
  const filesPrefetchMemoryRecordService = FilesPrefetchMemoryRecordService.getInstance();
  if (!filesPrefetchMemoryRecordService.getRootRenderStatus()) {
    return Promise.resolve(undefined);
  }

  const scenario = LoggingService.startNewScenario({
    scenarioName: scenarios.prefetchListData
  });
  if (!options.serverDataCallSignature) {
    scenario.incomplete({ message: scenarios.errorMessage.urlOrBodyMissing });
    return Promise.resolve(undefined);
  }
  const {
    serverDataCallSignature: { url, body }
  } = options;

  if (!body) {
    scenario.incomplete({ message: scenarios.errorMessage.bodyMissing });
    return Promise.resolve(undefined);
  }
  const hashCode = ListViewDataPrefetchHelper.createHashCode(url, body);
  // Save hashCode for update later as it would be needed for verification is list update callback.
  if (!filesPrefetchMemoryRecordService.isPrefetchStateUpdated(hashCode)) {
    filesPrefetchMemoryRecordService.populatePrefetchMemoryRecord(hashCode);
  }
  const prefetchCallPromise = filesPrefetchMemoryRecordService.getPrefetchPromiseForChannel(
    hashCode
  );
  /**
   * If prefetchCallPromise is pending, await until it finishes. Return the data right away saving a hit to IndexDB.
   * Also, reset the promise to undefined.
   */
  if (prefetchCallPromise) {
    return getFilesFromPrefetchCall(
      hashCode,
      prefetchCallPromise,
      filesPrefetchMemoryRecordService,
      scenario
    );
  }

  return getFilesFromIndexDB(hashCode, scenario);
}

/**
 * Given prefetch data from indexedDB, return final response to items view
 * @param prefetchedListViewData Data fetched from indexedDB
 * @param scenario scenario to be tracked
 */
function getPrefetchResponse(
  prefetchedListViewData: any,
  scenario: ITypedScenario<IEventData>
) {
  if (!prefetchedListViewData) {
    scenario.incomplete({
      message: scenarios.errorMessage.prefetchDataNotFound
    });
    return;
  }

  const finalResponse = {
    ...prefetchedListViewData,
    isFromCustomCache: true
  };

  scenario.mark(scenarios.steps.succesfullyPrefetchedData);
  scenario.stop();
  return finalResponse;
}

/**
 * Gets files from prefetch call
 * @param hashCode HashCode for current channel
 * @param prefetchCallPromise On-going prefetch call for channel
 * @param filesPrefetchMemoryRecordService In-memory record service
 * @param scenario Current tracking scenario
 * @returns Promise containing listData from prefetch call
 */
async function getFilesFromPrefetchCall(
  hashCode: string,
  prefetchCallPromise: Promise<any>,
  filesPrefetchMemoryRecordService: FilesPrefetchMemoryRecordService,
  scenario: ITypedScenario<IEventData>
): Promise<any> {
  try {
    const prefetchedListViewData = await prefetchCallPromise;

    if (isError(prefetchedListViewData)) {
      filesPrefetchMemoryRecordService.removePrefetchRecord(hashCode);
      scenario.fail({
        code: prefetchedListViewData.error.code,
        message: scenarios.errorMessage.errorResponseFromUpdateCallback
      });
      // If prefetch call failed, let items-view call RenderDataList.
      return Promise.resolve(undefined);
    }

    return getPrefetchResponse(prefetchedListViewData, scenario);
  } catch (e) {
    scenario.fail({
      message: scenarios.errorMessage.prefetchCallFailed
    });
    return Promise.resolve(undefined);
  }
}

function getFilesFromIndexDB(
  hashCode: string,
  scenario: ITypedScenario<IEventData>
) {
  return FilesCachePersistor.getInstance()
    .then(filesCachePersistor =>
      filesCachePersistor
        .getDataUsingKey(hashCode, FilesStoresTypes.teams_requests)
        .then(prefetchedListViewData =>
          getPrefetchResponse(prefetchedListViewData, scenario)
        )
        .catch(errorMessage => {
          scenario.fail({
            message: scenarios.errorMessage.indexedDbFetchFailed,
            itemsViewData: errorMessage
          });
        })
    )
    .catch(errorMessage => {
      scenario.fail({
        message: scenarios.errorMessage.cachePersistorLoadFailure,
        itemsViewData: errorMessage
      });
    });
}

/**
 * Called from items view when RenderListDataAsStream call is made. Teams uses this call
 * to update indexedDB if a matching hash code was found already saved in indexedDB.
 * We then update the response for the given hash code
 * @param requestUrl Request url for items view network call
 * @param requestBody Request payload body for items view network call
 * @param listDataResponse Response provided on network call for RenderListDataAsStream
 */
export function listDataUpdateCallback(
  requestUrl: string,
  requestBody: string | undefined,
  listDataResponse: string
): void {
  if (!requestBody) {
    return;
  }

  const scenario = LoggingService.startNewScenario({
    scenarioName: scenarios.listUpdateCallback
  });

  let data: any;
  try {
    data = JSON.parse(listDataResponse);
  } catch (e) {
    scenario.fail({ reason: scenarios.errorMessage.failToParseJSONResponse });
    return;
  }

  if (isError(data)) {
    scenario.fail({
      code: data.error.code,
      message: scenarios.errorMessage.errorResponseFromUpdateCallback
    });
    return;
  }

  const hashCode = ListViewDataPrefetchHelper.createHashCode(
    requestUrl,
    requestBody
  );

  // Async update the indexedDB using response data
  void updateIndexedDB(hashCode, scenario, data);
}

/**
 * Async function to check and update data in indexedDB. If hash code is found,
 * we will update the response. Otherwise we will ignore this response for
 * RenderListDataAsStream as it might not be for the root channel folder
 * @param hashCode hash code created using URL & Body
 * @param scenario scenario to be tracked
 * @param data Parsed data to be saved into indexedDB
 */
async function updateIndexedDB(
  hashCode: string,
  scenario: ITypedScenario<IEventData>,
  data: any
) {
  // If hash code is not present, it means that the data is not present in indexedDB
  if (
    !FilesPrefetchMemoryRecordService.getInstance().isPrefetchStateUpdated(
      hashCode
    )
  ) {
    scenario.incomplete({
      message: scenarios.errorMessage.indexedDbDataNotFound
    });
    return;
  }

  FilesCachePersistor.getInstance()
    .then(filesCachePersistor => {
      filesCachePersistor
        .persistData(hashCode, data, FilesStoresTypes.teams_requests)
        .then(() => {
          scenario.mark(scenarios.steps.successfullyUpdatedCache);
          scenario.stop();
        })
        .catch(errorMessage => {
          scenario.fail({
            message: scenarios.errorMessage.indexedDbPersistFailed,
            itemsViewData: errorMessage
          });
        });
    })
    .catch(errorMessage => {
      scenario.fail({
        message: scenarios.errorMessage.cachePersistorLoadFailure,
        itemsViewData: errorMessage
      });
    });
}

/**
 * @param parsedData Parsed API response from RenderListData call
 * @returns True if error
 */
function isError(parsedData: any): boolean {
  return !!parsedData && !!parsedData.error;
}
